import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createOnClientApproval,
  createOnClientApprovalResend,
  getAllBasketsThunk,
  getAllLookupValuesThunk,
  getBasketDetailsThunk,
  getBasketsByClientThunk,
  getNewBasketThunk,
  onBasketUpdateThunk,
  onOrderExecuteThunk,
} from './OrderEntryActions';
import { IBasketSecurity } from './OrderEntryForm.model';
import { ISecurity } from '../SwapsTek/OrderDetails/OrderDetailsSlice';
import { getSecurityDetailsThunk } from '../SwapsTek/OrderDetails/OrderDetailsActions';
import securityMasterData from './securityMaster.json';

export interface ILookupData {
  defaultValue: string;
  lookupValues: string[];
  tableId: number;
  tableName: string;
}
export interface IBasket {
  id: number;
  basketId: string;
  basketName: string;
  clientId: string;
  createdByUser: string;
  createdDate: string;
  modifiedByUser: string;
  modifiedDate: string;
  orderId: string;
  status: string;
  version: number;
  tenantId: string;
  basketItemDetails: {
    basketNotional: number;
    basketItems: IBasketItems[];
  };
  rollupItemDetails: {
    basketNotional: number;
    basketItems: IBasketItems[];
  };
}

export interface IBasketItems {
  id: number;
  basketItemId: string;
  basketId: string;
  version: string;
  security: string;
  bbgTickerExch: string;
  isin: string;
  sedol: string;
  quantity: number;
  substitutionQuantity: number;
  divRate: number;
  exDate: string;
  recordDate: string;
  price: number;
  fxRate: number;
  bargainRef: string;
  eligible: string;
  createdByUser: string;
  createdDate: string;
  inputCurrency?: string;
  securityName?: string;
  notional: number;
  swapCurrency?: string;
  tradeId: string;
  settlementDate?: string;
  tradeDate?: string;
  fundId?: string;
  location?: string;
  tradeSource?: string;
  tradeType?: string;
  tradeAction?: string;
  isNewlyAdded?: boolean;
  securityIssueCountry?: string;
  originalBasketId?: string;
  originalBasketItemId?: number;
  hasBelongToCurrentBasket?: boolean;
}
export interface IBasketDetails {
  id: number;
  basketId: string;
  basketName: string;
  version: number;
  clientId: string;
  status: string;
  orderId: string;
  tenantId: string;
  createdByUser: string;
  createdDate: string;
  currency?: string;
  basketItemDetails: {
    basketNotional: number;
    basketItems: IBasketItems[];
  };
  rollupItemDetails: {
    basketNotional: number;
    basketItems: IBasketItems[];
  };
  mergedItemDetails?: {
    basketNotional: number;
    basketItems: IBasketItems[];
  };
}

export interface OrderEntryData {
  allLookupValues: ILookupData[];
  allBaskets: IBasket[];
  selectedBasketDetails: IBasketDetails;
  loading: boolean;
  clientApprovalLoading: boolean;
  executeLoading: boolean;
  securityMaster: ISecurity[];
}

const initialState: OrderEntryData = {
  allLookupValues: [],
  allBaskets: [],
  selectedBasketDetails: {
    id: 0,
    basketId: '',
    basketName: '',
    version: 0,
    clientId: '',
    status: '',
    orderId: '',
    tenantId: '',
    createdByUser: '',
    createdDate: '',
    basketItemDetails: {
      basketNotional: 0,
      basketItems: [],
    },
    rollupItemDetails: {
      basketNotional: 0,
      basketItems: [],
    },
  },
  loading: false,
  clientApprovalLoading: false,
  executeLoading: false,
  securityMaster: securityMasterData as ISecurity[],
};

interface IUpdateBasketPayload {
  data: IBasketSecurity[];
  isNotNew: boolean;
}

export const updateBasket = (
  state: OrderEntryData,
  action: PayloadAction<IUpdateBasketPayload>,
) => {
  let basketDetails = { ...state.selectedBasketDetails };

  if (action.payload.isNotNew) {
    basketDetails = {
      ...basketDetails,
      rollupItemDetails: {
        ...basketDetails.rollupItemDetails,
        basketItems:
          basketDetails.rollupItemDetails?.basketItems?.map((b) => {
            const contextDetails = action.payload.data?.find((p) => p.id === b.basketItemId);
            return {
              ...b,
              price: contextDetails.price,
              fxRate: contextDetails.fxRate,
              divRate: contextDetails.dividendRate,
            };
          }) || [],
      },
    };
  } else {
    basketDetails = {
      ...basketDetails,
      mergedItemDetails: {
        ...basketDetails.mergedItemDetails,
        basketItems:
          basketDetails.mergedItemDetails?.basketItems?.map((b) => {
            let contextDetails = action.payload.data?.find((p) => p.id === b.basketItemId);
            if (!contextDetails) {
              contextDetails = action.payload.data?.find((p) => p.tradeId === b.tradeId);
            }

            return {
              ...b,
              price: contextDetails.price,
              fxRate: contextDetails.fxRate,
              divRate: contextDetails.dividendRate,
            };
          }) || [],
      },
    };
  }

  state.selectedBasketDetails = basketDetails;
};

export const resetBasket = (state: OrderEntryData) => {
  state.selectedBasketDetails = {
    id: 0,
    basketId: '',
    basketName: '',
    version: 0,
    clientId: '',
    status: '',
    orderId: '',
    tenantId: '',
    createdByUser: '',
    createdDate: '',
    basketItemDetails: {
      basketNotional: 0,
      basketItems: [],
    },
    rollupItemDetails: {
      basketNotional: 0,
      basketItems: [],
    },
  };
};

const orderEntrySlice = createSlice({
  name: 'Order Entry',
  initialState,
  reducers: {
    updateSelectedBasket: updateBasket,
    resetSelectedBasket: resetBasket,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLookupValuesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllLookupValuesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.allLookupValues = action.payload?.data?.allLookupValues || [];
      })
      .addCase(getAllLookupValuesThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllBasketsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllBasketsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.allBaskets = action.payload?.data?.allBaskets || [];
      })
      .addCase(getAllBasketsThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBasketsByClientThunk.pending, (state) => {
        state.loading = true;
        state.allBaskets = [];
      })
      .addCase(getBasketsByClientThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.allBaskets = action.payload?.data?.getBaskets || [];
      })
      .addCase(getBasketsByClientThunk.rejected, (state) => {
        state.loading = false;
        state.allBaskets = [];
      })
      .addCase(getBasketDetailsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBasketDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const basket = action.payload?.data?.basket;
        state.selectedBasketDetails = basket
          ? { ...basket, currency: action.meta.arg.params.currency }
          : {};
      })
      .addCase(getBasketDetailsThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getNewBasketThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNewBasketThunk.fulfilled, (state, action) => {
        state.loading = false;
        const basket = action.payload?.data?.getBasket;
        state.selectedBasketDetails = basket
          ? { ...basket, currency: action.meta.arg.params.currency }
          : {};
      })
      .addCase(getNewBasketThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(onOrderExecuteThunk.pending, (state) => {
        state.executeLoading = true;
      })
      .addCase(onOrderExecuteThunk.fulfilled, (state) => {
        state.executeLoading = false;
      })
      .addCase(onOrderExecuteThunk.rejected, (state) => {
        state.executeLoading = false;
      })
      .addCase(createOnClientApproval.pending, (state) => {
        state.clientApprovalLoading = true;
      })
      .addCase(createOnClientApproval.fulfilled, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(createOnClientApproval.rejected, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(onBasketUpdateThunk.pending, (state) => {
        state.clientApprovalLoading = true;
      })
      .addCase(onBasketUpdateThunk.fulfilled, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(onBasketUpdateThunk.rejected, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(createOnClientApprovalResend.pending, (state) => {
        state.clientApprovalLoading = true;
      })
      .addCase(createOnClientApprovalResend.fulfilled, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(createOnClientApprovalResend.rejected, (state) => {
        state.clientApprovalLoading = false;
      })
      .addCase(getSecurityDetailsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSecurityDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.securityMaster = action.payload.data.getSecurities;
      })
      .addCase(getSecurityDetailsThunk.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { updateSelectedBasket, resetSelectedBasket } = orderEntrySlice.actions;
export default orderEntrySlice.reducer;
